import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  TextField,
  Tabs,
  Tab,
  Snackbar,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { 
  Upload, 
  Video, 
  Bold, 
  Italic, 
  List, 
  Underline as UnderlineIcon,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Code,
  Quote,
  Undo,
  Redo,
  Palette,
  Type,
  Minus,
  Table,
  Link,
  Image,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import ImageExtension from "@tiptap/extension-image";
import LinkExtension from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { Node } from "@tiptap/core";
import { getWeekwiseIframeVideo } from "./api";
import Heading from "@tiptap/extension-heading";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";

// Custom Video Extension for proper rendering
const VideoNode = Node.create({
  name: "video",
  group: "block",
  atom: true,
  
  addAttributes() {
    return {
      src: { default: null },
      type: { default: "video/mp4" },
      controls: { default: true },
    };
  },

  parseHTML() {
    return [
      {
        tag: "video",
        getAttrs: (node) => ({
          src: node.querySelector("source")?.getAttribute("src"),
          type: node.querySelector("source")?.getAttribute("type") || "video/mp4",
          controls: node.hasAttribute("controls"),
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "video",
      {
        ...HTMLAttributes,
        controls: true,
        style: "max-width: 100%; border-radius: 8px; margin: 10px 0; background-color: #000;",
      },
      ["source", { src: HTMLAttributes.src, type: HTMLAttributes.type }],
    ];
  },

  addCommands() {
    return {
      insertVideo:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },
});

// Custom Iframe Extension for embedded videos
const IframeNode = Node.create({
  name: "iframe",
  group: "block",
  atom: true,
  
  addAttributes() {
    return {
      src: { default: null },
      width: { default: "640" },
      height: { default: "480" },
      allow: { default: "autoplay" },
      id: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: "iframe",
        getAttrs: (node) => ({
          src: node.getAttribute("src"),
          width: node.getAttribute("width") || "640",
          height: node.getAttribute("height") || "480",
          allow: node.getAttribute("allow") || "autoplay",
          id: node.getAttribute("id"),
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "iframe",
      {
        ...HTMLAttributes,
        style: "max-width: 100%; border-radius: 8px; margin: 10px 0; border: none;",
      },
    ];
  },

  addCommands() {
    return {
      insertIframe:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },
});

const TextEditor = ({
  content = "",
  onContentChange,
  loading = false,
  placeholder = "Write your content here...",
  showDebug = false,
}) => {
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [openTableDialog, setOpenTableDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [videoTab, setVideoTab] = useState(0);
  const [imageTab, setImageTab] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
const editor = useEditor({
  extensions: [
    StarterKit.configure({
      bulletList: false,
      orderedList: false,
      listItem: false,
      heading: false,
    }),
    Heading.configure({ levels: [1, 2, 3] }),
    BulletList,
    OrderedList,
    ListItem,
    Underline,
    ImageExtension.configure({ allowBase64: true }),
    LinkExtension,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    VideoNode,
    IframeNode,
  ],

    content: content,
    onUpdate: ({ editor }) => {
      onContentChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none',
        style: 'min-height: 300px; outline: none;',
      },
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, false);
    }
  }, [content, editor]);

  // Upload video and get iframe
  const uploadVideoAndGetIframe = async (file) => {
    try {
      setUploading(true);
      setUploadError("");
      const response = await getWeekwiseIframeVideo(file);
      
      if (response && typeof response === 'string' && response.includes('<iframe')) {
        return response;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Video upload error:", error);
      throw new Error("Failed to upload video. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Handle video upload from file
  const handleVideoFileUpload = async () => {
    if (!videoFile) {
      setUploadError("Please select a video file");
      return;
    }

    try {
      const iframeHtml = await uploadVideoAndGetIframe(videoFile);
      editor.commands.insertContent(iframeHtml);
      resetVideoDialog();
      showSnackbar("Video uploaded and inserted successfully", "success");
    } catch (error) {
      setUploadError(error.message);
    }
  };

  // Handle video insertion from URL
  const handleVideoUrlInsert = () => {
    if (!videoUrl.trim()) {
      setUploadError("Please enter a video URL");
      return;
    }

    try {
      const videoIframe = `<iframe src="${videoUrl}" width="640" height="360" frameborder="0" allowfullscreen style="max-width: 100%; border-radius: 8px; margin: 10px 0;"></iframe>`;
      editor.commands.insertContent(videoIframe);
      resetVideoDialog();
      showSnackbar("Video inserted successfully", "success");
    } catch (error) {
      setUploadError("Failed to insert video. Please try again.");
      console.error("Video insertion error:", error);
    }
  };

  
  // Heading functions
  const handleHeadingChange = (level) => {
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level }).run();
    }
  };

  // List functions
  const handleBulletList = () => {
    editor.chain().focus().toggleBulletList().run();
  };

  const handleOrderedList = () => {
    editor.chain().focus().toggleOrderedList().run();
  };

  // Alignment functions
  const handleTextAlign = (alignment) => {
    editor.chain().focus().setTextAlign(alignment).run();
  };

  // Other formatting functions
  const handleStrike = () => {
    editor.chain().focus().toggleStrike().run();
  };

  const handleCode = () => {
    editor.chain().focus().toggleCode().run();
  };

  const handleCodeBlock = () => {
    editor.chain().focus().toggleCodeBlock().run();
  };

  const handleBlockquote = () => {
    editor.chain().focus().toggleBlockquote().run();
  };

  const handleHorizontalRule = () => {
    editor.chain().focus().setHorizontalRule().run();
  };

  // Simple text color using inline styles
  const handleTextColorChange = () => {
    const color = prompt("Enter hex color (e.g., #ff0000):", "#000000");
    if (color) {
      document.execCommand('styleWithCSS', false, true);
      document.execCommand('foreColor', false, color);
    }
  };

  // Simple highlight using background color
  const handleHighlightColorChange = () => {
    const color = prompt("Enter highlight color (e.g., #ffff00):", "#ffff00");
    if (color) {
      document.execCommand('styleWithCSS', false, true);
      document.execCommand('hiliteColor', false, color);
    }
  };

  const handleUndo = () => {
    editor.chain().focus().undo().run();
  };

  const handleRedo = () => {
    editor.chain().focus().redo().run();
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Existing functions for image, link, etc.
  const handleInsertImage = () => {
    setOpenImageDialog(true);
  };

  const handleInsertImageConfirm = () => {
    if (imageTab === 0 && imagePreview) {
      editor.chain().focus().setImage({ src: imagePreview }).run();
      resetImageDialog();
    } else if (imageTab === 1 && imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      resetImageDialog();
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    setUploadError("");

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError("Please select a valid image file (JPEG, PNG, GIF, etc.)");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError("Image file size must be less than 10MB");
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleInsertLink = () => {
    const previousUrl = editor.getAttributes('link').href || '';
    setLinkUrl(previousUrl);
    setOpenLinkDialog(true);
  };

  const handleInsertLinkConfirm = () => {
    if (linkUrl) {
      const formattedUrl = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      editor.chain().focus()
        .extendMarkRange('link')
        .setLink({ href: formattedUrl })
        .run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setOpenLinkDialog(false);
    setLinkUrl("");
  };

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setOpenLinkDialog(false);
    setLinkUrl("");
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    setUploadError("");

    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setUploadError("Please select a valid video file (MP4, WebM, OGG, etc.)");
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      setUploadError("Video file size must be less than 100MB");
      return;
    }

    setVideoFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setVideoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const resetImageDialog = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl("");
    setOpenImageDialog(false);
    setUploadError("");
    setImageTab(0);
  };

  const resetVideoDialog = () => {
    setVideoFile(null);
    setVideoPreview(null);
    setVideoUrl("");
    setOpenVideoDialog(false);
    setUploading(false);
    setUploadError("");
    setVideoTab(0);
  };

  const handleVideoTabChange = (event, newValue) => {
    setVideoTab(newValue);
    setUploadError("");
  };

  const handleImageTabChange = (event, newValue) => {
    setImageTab(newValue);
    setUploadError("");
  };

  if (loading || !editor) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Initializing editor...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Enhanced Toolbar */}
      <Card elevation={2} sx={{ mb: 2, p: 1 }}>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
          
          {/* Undo/Redo */}
          <Tooltip title="Undo">
            <IconButton size="small" onClick={handleUndo}>
              <Undo size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Redo">
            <IconButton size="small" onClick={handleRedo}>
              <Redo size={18} />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Headings */}
          <Tooltip title="Normal Text">
            <IconButton 
              size="small" 
              onClick={() => handleHeadingChange(0)}
              color={editor.isActive('paragraph') ? "primary" : "default"}
            >
              <Type size={18} />
            </IconButton>
          </Tooltip>
          {[1, 2, 3].map(level => (
            <Tooltip key={level} title={`Heading ${level}`}>
              <IconButton 
                size="small"
                onClick={() => handleHeadingChange(level)}
                color={editor.isActive('heading', { level }) ? "primary" : "default"}
              >
                H{level}
              </IconButton>
            </Tooltip>
          ))}

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Basic Formatting */}
          <Tooltip title="Bold">
            <IconButton 
              size="small" 
              onClick={() => editor.chain().focus().toggleBold().run()}
              color={editor.isActive('bold') ? "primary" : "default"}
            >
              <Bold size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic">
            <IconButton 
              size="small" 
              onClick={() => editor.chain().focus().toggleItalic().run()}
              color={editor.isActive('italic') ? "primary" : "default"}
            >
              <Italic size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Underline">
            <IconButton 
              size="small" 
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              color={editor.isActive('underline') ? "primary" : "default"}
            >
              <UnderlineIcon size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Strikethrough">
            <IconButton 
              size="small" 
              onClick={handleStrike}
              color={editor.isActive('strike') ? "primary" : "default"}
            >
              <Strikethrough size={18} />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />


          {/* Alignment */}
          <Tooltip title="Align Left">
            <IconButton 
              size="small" 
              onClick={() => handleTextAlign('left')}
              color={editor.isActive({ textAlign: 'left' }) ? "primary" : "default"}
            >
              <AlignLeft size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Center">
            <IconButton 
              size="small" 
              onClick={() => handleTextAlign('center')}
              color={editor.isActive({ textAlign: 'center' }) ? "primary" : "default"}
            >
              <AlignCenter size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Right">
            <IconButton 
              size="small" 
              onClick={() => handleTextAlign('right')}
              color={editor.isActive({ textAlign: 'right' }) ? "primary" : "default"}
            >
              <AlignRight size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Justify">
            <IconButton 
              size="small" 
              onClick={() => handleTextAlign('justify')}
              color={editor.isActive({ textAlign: 'justify' }) ? "primary" : "default"}
            >
              <AlignJustify size={18} />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Lists */}
          <Tooltip title="Bullet List">
            <IconButton 
              size="small" 
              onClick={handleBulletList}
              color={editor.isActive('bulletList') ? "primary" : "default"}
            >
              <List size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Numbered List">
            <IconButton 
              size="small" 
              onClick={handleOrderedList}
              color={editor.isActive('orderedList') ? "primary" : "default"}
            >
              1.
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Special Formatting */}
          <Tooltip title="Code">
            <IconButton 
              size="small" 
              onClick={handleCode}
              color={editor.isActive('code') ? "primary" : "default"}
            >
              <Code size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Code Block">
            <IconButton 
              size="small" 
              onClick={handleCodeBlock}
              color={editor.isActive('codeBlock') ? "primary" : "default"}
            >
              <Code size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Blockquote">
            <IconButton 
              size="small" 
              onClick={handleBlockquote}
              color={editor.isActive('blockquote') ? "primary" : "default"}
            >
              <Quote size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Horizontal Line">
            <IconButton size="small" onClick={handleHorizontalRule}>
              <Minus size={18} />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Insert Elements */}
          <Tooltip title="Insert Link">
            <IconButton 
              size="small" 
              onClick={handleInsertLink}
              color={editor.isActive('link') ? "primary" : "default"}
            >
              <Link size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert Image">
            <IconButton size="small" onClick={handleInsertImage}>
              <Image size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert Video">
            <IconButton size="small" onClick={() => setOpenVideoDialog(true)}>
              <Video size={18} />
            </IconButton>
          </Tooltip>
         
        </Box>
      </Card>


      {/* Rest of your existing dialogs (Link, Image, Video) remain the same */}
      {/* Link Dialog */}
      <Dialog open={openLinkDialog} onClose={() => setOpenLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="URL"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              helperText="Enter the URL you want to link to"
              autoFocus
            />
            {editor?.isActive('link') && (
              <Alert severity="info">
                Current link: {editor.getAttributes('link').href}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {editor?.isActive('link') && (
            <Button onClick={handleRemoveLink} color="error">
              Remove Link
            </Button>
          )}
          <Button onClick={() => setOpenLinkDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleInsertLinkConfirm} 
            variant="contained"
          >
            {editor?.isActive('link') ? 'Update Link' : 'Insert Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Dialog - Keep existing */}
      <Dialog open={openImageDialog} onClose={resetImageDialog} maxWidth="md" fullWidth>
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {uploadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {uploadError}
              </Alert>
            )}

            <Tabs value={imageTab} onChange={handleImageTabChange} sx={{ mb: 3 }}>
              <Tab label="Upload File" />
              <Tab label="From URL" />
            </Tabs>

            {imageTab === 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    border: "2px dashed",
                    borderColor: imageFile ? "success.main" : "grey.400",
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    backgroundColor: imageFile ? "success.light" : "grey.50",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                  component="label"
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    style={{ display: "none" }}
                  />
                  <Upload size={48} style={{ color: "#1976d2", marginBottom: 16 }} />
                  <Typography variant="h6" gutterBottom>
                    {imageFile ? "Image Selected" : "Click to Upload Image"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {imageFile 
                      ? `Selected: ${imageFile.name}`
                      : "Drag & drop or click to select an image file"
                    }
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                    Supported formats: JPEG, PNG, GIF, WebP • Max size: 10MB
                  </Typography>
                </Box>

                {imagePreview && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                      Image Preview:
                    </Typography>
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px', 
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        display: 'block'
                      }}
                    />
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        File: {imageFile?.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Size: {(imageFile?.size / (1024 * 1024)).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Image URL"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  helperText="Enter the URL of the image you want to insert"
                />
                {imageUrl && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                      Image Preview:
                    </Typography>
                    <img 
                      src={imageUrl} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px', 
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={resetImageDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleInsertImageConfirm}
            variant="contained"
            disabled={
              (imageTab === 0 ? !imagePreview : !imageUrl.trim())
            }
          >
            Insert Image
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Upload Dialog - Keep existing */}
      <Dialog open={openVideoDialog} onClose={resetVideoDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Video size={24} />
          Insert Video
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {uploadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {uploadError}
              </Alert>
            )}

            <Tabs value={videoTab} onChange={handleVideoTabChange} sx={{ mb: 3 }}>
              <Tab label="Upload File" />
              <Tab label="From URL" />
            </Tabs>

            {videoTab === 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    border: "2px dashed",
                    // borderColor: videoFile ? "success.main" : "grey.400",
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    backgroundColor: videoFile ? "success.light" : "grey.50",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                  component="label"
                >
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoFileChange}
                    style={{ display: "none" }}
                    disabled={uploading}
                  />
                  <Upload size={28} style={{ color: "#1976d2", marginBottom: 16 }} />
                  <Typography variant="h6" gutterBottom>
                    {videoFile ? "Video Selected" : "Click to Upload Video"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {videoFile 
                      ? `Selected: ${videoFile.name}`
                      : "Drag & drop or click to select a video file"
                    }
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                    Supported formats: MP4, WebM, OGG • Max size: 100MB
                  </Typography>
                </Box>

                {videoPreview && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                      Video Preview:
                    </Typography>
                    <video 
                      width="100%" 
                      height="auto" 
                      controls 
                      style={{ 
                        borderRadius: 8, 
                        backgroundColor: "#000",
                        maxHeight: "300px"
                      }}
                    >
                      <source src={videoPreview} type={videoFile?.type} />
                    </video>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        File: {videoFile?.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Size: {(videoFile?.size / (1024 * 1024)).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Video URL"
                  placeholder="https://example.com/video.mp4"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  disabled={uploading}
                  helperText="Enter a direct link to a video file (MP4, WebM, etc.)"
                />

                {videoUrl && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                      Video Preview:
                    </Typography>
                    <video 
                      width="100%" 
                      height="auto" 
                      controls 
                      style={{ 
                        borderRadius: 8, 
                        backgroundColor: "#000",
                        maxHeight: "300px"
                      }}
                    >
                      <source src={videoUrl} type="video/mp4" />
                    </video>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={resetVideoDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={videoTab === 0 ? handleVideoFileUpload : handleVideoUrlInsert}
            variant="contained"
            disabled={
              uploading || 
              (videoTab === 0 ? !videoFile : !videoUrl.trim())
            }
            startIcon={uploading ? <CircularProgress size={16} /> : null}
          >
            {uploading ? "Uploading..." : "Insert Video"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Editor Content */}
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box 
            sx={{ 
              p: 2,
              height: "400px",
              overflow: "auto",
              '& .ProseMirror': {
                minHeight: "350px",
                outline: 'none',
                fontSize: '16px',
                lineHeight: '1.6',
                // '& h1, h2, h3, h4, h5, h6': {
                //   margin: '0.5em 0',
                // },
                '& h1': {
  fontSize: '2rem',
  fontWeight: 700,
},
'& h2': {
  fontSize: '1.6rem',
  fontWeight: 600,
},
'& h3': {
  fontSize: '1.3rem',
  fontWeight: 600,
},

                '& p': {
                  margin: '0.5em 0',
                },
               
                '& ul': {
  listStyleType: 'disc',
  paddingLeft: '2rem',
},
'& ol': {
  listStyleType: 'decimal',
  paddingLeft: '2rem',
},


                '& blockquote': {
                  borderLeft: '3px solid #ddd',
                  margin: '1em 0',
                  paddingLeft: '1em',
                  fontStyle: 'italic',
                },
                '& pre': {
                  background: '#1a1a1a',
                  color: '#fff',
                  padding: '1em',
                  borderRadius: '4px',
                  overflow: 'auto',
                  margin: '0.5em 0',
                },
                '& code': {
                  background: '#f5f5f5',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  fontSize: '0.9em',
                },
                '& table': {
                  borderCollapse: 'collapse',
                  margin: '0.5em 0',
                  width: '100%',
                  '& td, & th': {
                    border: '1px solid #ddd',
                    padding: '8px',
                    textAlign: 'left',
                  },
                  '& th': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                  },
                },
                '& .editor-image': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px', 
                  margin: '0.5em 0',
                  display: 'block',
                },
                '& .editor-link': {
                  color: '#1976d2',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                },
                '& video': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  margin: '10px 0',
                  backgroundColor: '#000',
                  cursor: 'pointer',
                  display: 'block',
                },
                '& iframe': {
                  maxWidth: '100%',
                  borderRadius: '8px',
                  margin: '10px 0',
                  border: 'none',
                  display: 'block',
                },
                '& a': {
                  color: '#1976d2',
                  textDecoration: 'underline',
                },
                '& hr': {
                  border: 'none',
                  borderTop: '2px solid #ddd',
                  margin: '1em 0',
                },
              },
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a8a8a8',
              },
            }}
          >
            <EditorContent editor={editor} />
          </Box>
        </CardContent>
      </Card>

      {/* HTML Debug */}
      {showDebug && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            HTML Content (Debug)
          </Typography>
          <Card elevation={1}>
            <CardContent
              sx={{
                backgroundColor: "#f9f9f9",
                minHeight: "60px",
                maxHeight: "150px",
                fontFamily: "monospace",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                overflow: "auto",
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#e0e0e0',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#9e9e9e',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#757575',
                },
              }}
            >
              {editor.getHTML() || "<Empty content>"}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TextEditor;